import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, Modal, Select } from 'antd';
import { IPartyForUser, Stream, StreamType } from '../../PartyClientResponse';
import { ActionType, useAppState } from '../../state/AppState';
import { gql, useMutation } from '@apollo/client';
import ANTStreamPreview from './ANTStreamPreview';
import BackroomVideo from '../video/BackroomVideo';
import AudioPeakMeter from './AudioPeakMeter2';
import { ReactComponent as PlusIcon } from '../party/icons/plus-sign.svg';
import _ from 'lodash';

type AudioSourceType = 'audio-follows-video' | 'alpha' | 'bravo' | 'charlie' | 'delta' | 'echo';

export const channels = [
  'alpha',
  'bravo',
  'charlie',
  'delta',
  'echo',
  'foxtrot',
  'golf',
  'hotel',
  'india',
  'juliet',
  'kilo',
  'lima',
  'mike',
  'november',
  'oscar',
  'papa',
  'quebec',
  'romeo',
  'sierra',
  'tango',
  'uniform',
  'victor',
  'whisky',
  'xray',
  'yankee',
  'zulu',
];

export type VideoStats = Partial<
  {
    bandwidth: number;
    originalWidth: number;
    originalHeight: number;
    corruptedVideoFrames: number; // unlisted but comes from VideoPlaybackQuality
  } & VideoPlaybackQuality
>;

const VideoWithAudioMeter = ({
  height,
  id,
  muted,
  transitionLength,
}: {
  height: number | string;
  id: string;
  muted: boolean;
  transitionLength: number;
}) => {
  const [, setStats] = useState<VideoStats>({
    bandwidth: 0,
    originalHeight: 0,
    originalWidth: 0,
    corruptedVideoFrames: 0,
    creationTime: 0,
    droppedVideoFrames: 0,
    totalVideoFrames: 0,
  });
  const meterDivRef = useRef<HTMLDivElement | null>(null);
  const audioMeterRef = useRef<AudioPeakMeter | null>(null);
  const [videoEl, onLoadVideoElement] = useState<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (audioMeterRef.current) {
      audioMeterRef.current.setMuted(muted);
    }
  }, [muted, audioMeterRef]);

  useEffect(() => {
    const meter = audioMeterRef.current;
    if (videoEl && meterDivRef.current && !audioMeterRef.current) {
      audioMeterRef.current = new AudioPeakMeter(
        meterDivRef.current,
        videoEl,
        {
          backgroundColor: '#b37194',
          borderSize: 6,
        },
        id,
        muted
      );
      // @ts-ignore
      // meterRef.current.firstChild.style.backgroundColor = 'transparent';
      // // @ts-ignore
      // meterRef.current.firstChild?.childNodes.forEach((child) => {
      //   // @ts-ignore
      //   if (child.style.backgroundColor === 'black') {
      //     // @ts-ignore
      //     child.style.backgroundColor = 'transparent';
      //   }
      // });
    }
    return () => {
      if (meter) {
        meter?.destroy();
        audioMeterRef.current = null;
      }
    };
  }, [videoEl, meterDivRef, audioMeterRef, id, muted]);

  return (
    <div style={{ height: '100%', display: 'flex' }}>
      <div key={id} style={{ width: 50, height: '100%', marginRight: 20 }} ref={meterDivRef}></div>
      <div style={{ height: '100%' }}>
        <BackroomVideo
          onStatsUpdated={setStats}
          onLoadVideoElement={onLoadVideoElement}
          // muted={party.stage.stream.backroomStreamId !== audioMonitor}
          // previewStream.backroomStreamId === audioMonitor ? 1 : 0
          controls={false}
          muted={false}
          height={height}
          width={'100%'}
          id={id}
          transitionLength={transitionLength}
        />
      </div>
    </div>
  );
};

const MonitorButton = ({
  prevWindow,
  audioMonitor,
  onClick,
}: {
  prevWindow: string;
  audioMonitor: string;
  onClick: (id: string) => void;
}) => (
  <img
    onClick={() => onClick(prevWindow)}
    style={{
      marginRight: '0.5em',
      cursor: 'pointer',
      filter: audioMonitor === prevWindow ? 'none' : 'grayscale(100%)',
    }}
    alt='mic'
    src='/icons/mic.svg'
    width={40}
    height={40}></img>
);

const Cockpit = ({ party }: { party: IPartyForUser }) => {
  const { dispatch } = useAppState();
  const [modal, contextHolder] = Modal.useModal();
  const [previewStream, setPreviewStream] = useState<Stream>(party.stage.stream);
  const [audioMonitor, setAudioMonitor] = useState<string>('main');
  const [audioSource, setAudioSource] = useState<AudioSourceType>('audio-follows-video');
  const [transitionLengthInput, setTransitionLengthInput] = useState<number>(
    (party.stage.transitionLength || 5000) / 1000
  );
  const channels = _.uniq((party.stage.streamKeys || []).concat('demo'));
  const [updateParty, { loading: partyUpdateLoading }] = useMutation(gql`
    mutation EditPartyStage(
      $key: String!
      $streamType: Int
      $backroomStreamId: String
      $transitionLength: Float
      $audioSource: String
      $addStreamKey: Boolean
      $deleteStreamKey: String
    ) {
      updateParty(
        input: {
          key: $key
          streamType: $streamType
          backroomStreamId: $backroomStreamId
          transitionLength: $transitionLength
          audioSource: $audioSource
          addStreamKey: $addStreamKey
          deleteStreamKey: $deleteStreamKey
        }
      ) {
        success
      }
    }
  `);

  const setAntSourceActive = useCallback(
    (videoId: string) => {
      updateParty({
        variables: {
          key: party.key,
          streamType: StreamType.Backroom,
          backroomStreamId: videoId,
          transitionLength: transitionLengthInput * 1000,
          audioSource,
        },
      });
    },
    [updateParty, transitionLengthInput, party.key, audioSource]
  );

  return (
    <div className='App'>
      <div
        style={{
          display: 'flex',
          width: '100%',
          height: '100vh',
          flexDirection: 'column',
        }}>
        <div
          style={{
            display: 'flex',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'space-around',
          }}>
          <div
            style={{
              // display: 'flex',
              // flexDirection: 'column',
              height: '100%',
              // width: 400,
              padding: 24,
              overflowY: 'scroll',
            }}>
            {channels
              .sort((a, b) => {
                return a.localeCompare(b);
              })
              .map((videoId, idx) => {
                return (
                  <ANTStreamPreview
                    streamIndex={idx}
                    partyUpdateLoading={partyUpdateLoading}
                    key={videoId}
                    videoId={videoId}
                    party={party}
                    muted
                    setPreviewStream={setPreviewStream}
                    previewStream={previewStream}
                    deleteStream={() => {
                      updateParty({
                        variables: {
                          key: party.key,
                          deleteStreamKey: videoId,
                        },
                      });
                    }}
                  />
                );
              })}
            <div
              className='new-stream-button'
              onClick={() => {
                updateParty({
                  variables: {
                    key: party.key,
                    addStreamKey: true,
                  },
                });
              }}>
              <PlusIcon
                style={{
                  height: 24,
                  width: 24,
                  marginRight: 20,
                }}
              />{' '}
              New stream key
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
              height: '100%',
              width: 'auto',
              padding: 24,
              flexDirection: 'column',
            }}>
            <div style={{ height: '40%' }}>
              <h2 style={{ color: 'white' }}>
                <MonitorButton
                  onClick={setAudioMonitor}
                  audioMonitor={audioMonitor}
                  prevWindow='main'
                />
                Live stream
              </h2>
              <div style={{ height: `calc(100% - ${44}px)` }}>
                {party.stage.stream.type === StreamType.Backroom && (
                  <VideoWithAudioMeter
                    height={'100%'}
                    id={party.stage.stream.backroomStreamId}
                    muted={audioMonitor !== 'main'}
                    transitionLength={party.stage.transitionLength}
                  />
                )}
              </div>
            </div>
            <div style={{ height: '40%' }}>
              <h2 style={{ color: 'white' }}>
                <MonitorButton
                  onClick={setAudioMonitor}
                  audioMonitor={audioMonitor}
                  prevWindow='preview'
                />
                Preview stream
              </h2>
              <div style={{ height: `calc(100% - ${44}px)` }}>
                {previewStream.type === StreamType.Backroom && (
                  <VideoWithAudioMeter
                    transitionLength={200}
                    id={previewStream.backroomStreamId}
                    // volume={previewStream.backroomStreamId === audioMonitor ? 1 : 0}
                    muted={audioMonitor !== 'preview'}
                    height={'100%'}
                  />
                )}
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              height: '100%',
              width: 400,
              padding: 24,
              flexDirection: 'column',
            }}>
            <div style={{ height: '25%' }}>
              <Button
                loading={partyUpdateLoading}
                onClick={() => {
                  dispatch({ type: ActionType.ToggleCockpitMode, enable: false });
                }}>
                Back to the party
              </Button>
            </div>
            <div style={{ height: '50%' }}>
              {(party.stage.stream.type !== StreamType.Backroom ||
                (previewStream.type === StreamType.Backroom &&
                  previewStream.backroomStreamId !== party.stage.stream.backroomStreamId)) && (
                <Button
                  loading={partyUpdateLoading}
                  type='primary'
                  size='large'
                  onClick={() => {
                    if (previewStream.type === StreamType.Backroom) {
                      setAntSourceActive(previewStream.backroomStreamId);
                    }
                  }}>
                  transition to{' '}
                  {previewStream.type === StreamType.Backroom
                    ? `stream ${channels.indexOf(previewStream.backroomStreamId)}`
                    : null}
                </Button>
              )}
              <br></br>
              <br></br>
              <Input
                type='text'
                style={{ width: 60, textAlign: 'center', marginRight: 20 }}
                value={transitionLengthInput}
                onChange={(e) => setTransitionLengthInput(Number(e.target.value || 0))}
              />
              <label>transition length (seconds)</label>
              <br></br>
              <br></br>
              <Select
                style={{ textAlign: 'center', marginRight: 20 }}
                value={audioSource}
                onChange={(value) => setAudioSource(value as unknown as AudioSourceType)}>
                <Select.Option value='audio-follows-video'>Audio follows video</Select.Option>
                {channels.map((channel) => (
                  <Select.Option value={channel}>{channel}</Select.Option>
                ))}
              </Select>
              <label>audio source</label>
            </div>
            <div style={{ height: '25%' }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cockpit;
